import { AppUnlockScreen } from "@components/brand/AppUnlockScreen";
import { PhoneFrame } from "@components/brand/PhoneFrame";
import { AppCta } from "@components/home/AppCta";
import { Estimator } from "@components/home/Estimator";
import { Faq } from "@components/home/Faq";
import { FlexStrip } from "@components/home/FlexStrip";
import { Page } from "@components/layout/Page";
import { Checklist } from "@components/product/Blocks";
import { ProductHero } from "@components/product/ProductHero";
import { Steps } from "@components/shared/Steps";
import { StoreBadges } from "@components/shared/StoreBadges";
import { UNLOCK } from "@/lib/site";
import { track } from "@services/analytics";
import { useTranslations } from "@services/strings";

const UnlockPage = () => {
  const { t, formatNumber, formatPercent } = useTranslations();

  const vars = {
    ltv: formatPercent(UNLOCK.maxLoanToValue),
    maxAmount: formatNumber(UNLOCK.maxAmount),
    maxAmountM: formatNumber(UNLOCK.maxAmount / 1_000_000),
    minAmount: formatNumber(UNLOCK.minAmount),
    maxYears: UNLOCK.maxYears,
    approvalDays: UNLOCK.approvalDays,
  };

  return (
    <Page
      title={t("site.unlock.meta.title")}
      description={t("site.unlock.meta.description", vars)}
    >
      <ProductHero
        placement="unlock-hero"
        eyebrow={t("site.unlock.hero.eyebrow")}
        title={t("site.unlock.hero.title")}
        subtitle={t("site.unlock.hero.subtitle", vars)}
        facts={(["ltv", "amount", "years", "days"] as const).map((id) => ({
          value: t(`site.unlock.facts.${id}.value`, vars),
          label: t(`site.unlock.facts.${id}.label`),
        }))}
        cta={
          <div className="flex flex-wrap items-center gap-3">
            <a
              href="#estimator"
              onClick={() =>
                track({
                  event: "Button Clicked",
                  properties: {
                    label: "Unlock hero - See what you could unlock",
                  },
                })
              }
              className="inline-flex h-11 items-center justify-center rounded-full bg-ink px-5 text-[15px] font-semibold text-white transition-colors hover:bg-navy"
            >
              {t("site.common.seeEstimate")}
            </a>
            <StoreBadges placement="unlock-hero" />
          </div>
        }
        aside={
          <PhoneFrame>
            <AppUnlockScreen />
          </PhoneFrame>
        }
      />

      <Checklist
        tone="mist"
        title={t("site.unlock.eligibility.title")}
        columns={1}
        items={(["property", "income", "identity"] as const).map((id) =>
          t(`site.unlock.eligibility.items.${id}`),
        )}
      />

      <Estimator />

      <FlexStrip
        placement="unlock"
        title={t("site.unlock.flexStrip.title")}
        body={t("site.unlock.flexStrip.body")}
        cta={t("site.unlock.flexStrip.cta")}
      />

      <Steps
        tone="mist"
        title={t("site.unlock.steps.title")}
        steps={(["one", "two", "three", "four"] as const).map((id) => ({
          title: t(`site.unlock.steps.items.${id}.title`),
          body: t(`site.unlock.steps.items.${id}.body`, vars),
        }))}
      />

      <Faq
        title={t("site.unlock.faq.title")}
        items={(
          [
            "cost",
            "min",
            "security",
            "rent",
            "early",
            "jointly",
            "abroad",
          ] as const
        ).map((id) => ({
          id,
          question: t(`site.unlock.faq.items.${id}.q`),
          answer: t(`site.unlock.faq.items.${id}.a`, vars),
        }))}
      />

      <AppCta
        placement="unlock-cta"
        title={t("site.home.cta.title")}
        body={t("site.home.cta.body")}
      />
    </Page>
  );
};

export default UnlockPage;
